import * as THREE from "three";

/**
 * Placed FURNITURE (bed / safebox — Okka Furniture stock). Pure procedural low-poly props in the
 * codebase's box-mesh style, plus a small render LAYER that mirrors the synced
 * `Building.furniture` lists into the right zone groups:
 *   - business interiors are far-away rooms at absolute world coords, so a piece's synced x/y
 *     (server px) map straight to world x/z;
 *   - the 4 apartment HOMES live inside the tenement at real elevation, so the layer adds the
 *     storey's floor height (world3d passes an `elevFor` resolver).
 * The `z` component is the player-chosen lift above the room floor (world units, clamped 0..2).
 */

const U = 1 / 32; // world units per server px (mirrors streetScene's U)

/** Build a furniture prop for an item id (origin at its floor point). Unknown ids get a crate. */
export function furnitureMesh(itemId: string): THREE.Group {
  const g = new THREE.Group();
  const box = (w: number, h: number, d: number, color: number, x: number, y: number, z: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshStandardMaterial({ color }));
    m.position.set(x, y, z); g.add(m);
    return m;
  };
  if (itemId === "bed") {
    // Dark wood frame, pale mattress, pillow at the head (−z), warm blanket over the foot end.
    box(1.1, 0.3, 2.0, 0x4a3320, 0, 0.25, 0);                 // frame
    for (const [lx, lz] of [[-0.48, -0.9], [0.48, -0.9], [-0.48, 0.9], [0.48, 0.9]] as const) {
      box(0.1, 0.2, 0.1, 0x33241a, lx, 0.1, lz);              // legs
    }
    box(1.1, 0.5, 0.14, 0x4a3320, 0, 0.55, -0.99);            // headboard
    box(1.0, 0.16, 1.86, 0xe8e2d4, 0, 0.48, 0);               // mattress
    box(0.72, 0.12, 0.4, 0xf4f0e6, 0, 0.61, -0.62);           // pillow
    box(1.04, 0.1, 1.05, 0x8a3a3a, 0, 0.58, 0.42);            // blanket
  } else if (itemId === "safebox") {
    // A squat steel safe: dark body, proud door plate, brass dial + handle, low feet.
    box(0.8, 0.9, 0.7, 0x3a3d44, 0, 0.5, 0);                  // body
    box(0.62, 0.66, 0.06, 0x4a4e57, 0, 0.52, 0.36);           // door plate (front, +z)
    const dial = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.06, 12), new THREE.MeshStandardMaterial({ color: 0xb9892f, metalness: 0.6, roughness: 0.4 }));
    dial.rotation.x = Math.PI / 2; dial.position.set(-0.12, 0.56, 0.41); g.add(dial);
    box(0.05, 0.22, 0.05, 0xb9892f, 0.16, 0.52, 0.41);        // handle bar
    for (const [fx, fz] of [[-0.3, -0.25], [0.3, -0.25], [-0.3, 0.25], [0.3, 0.25]] as const) {
      box(0.12, 0.1, 0.12, 0x24262b, fx, 0.05, fz);           // feet
    }
  } else if (itemId === "furnace") {
    // A squat brick smelter: stone plinth, brick body, tapered crown, chimney stub, and a
    // glowing fire mouth on the front (+z) — the Black Smith's smelting station.
    box(1.0, 0.2, 0.9, 0x55575e, 0, 0.1, 0);                  // stone plinth
    box(0.84, 0.9, 0.74, 0x7a4636, 0, 0.65, 0);               // brick body
    box(0.7, 0.3, 0.6, 0x6b3d2f, 0, 1.25, 0);                 // tapered crown
    box(0.24, 0.5, 0.24, 0x5d5f66, 0.18, 1.6, -0.15);         // chimney stub
    const mouth = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.3),
      new THREE.MeshStandardMaterial({ color: 0xff8a2a, emissive: 0xff6a1a, emissiveIntensity: 1.2 }));
    mouth.position.set(0, 0.5, 0.38); g.add(mouth);
  } else if (itemId === "anvil") {
    // A steel anvil on a wooden stump: base, waist, working face + a horn off the east side.
    box(0.7, 0.35, 0.6, 0x4a3320, 0, 0.175, 0);               // wooden stump
    box(0.5, 0.16, 0.4, 0x3a3d44, 0, 0.43, 0);                // anvil base
    box(0.3, 0.18, 0.26, 0x33363c, 0, 0.6, 0);                // waist
    box(0.8, 0.16, 0.34, 0x4c505a, 0, 0.77, 0);               // face
    const horn = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.34, 8), new THREE.MeshStandardMaterial({ color: 0x4c505a }));
    horn.rotation.z = -Math.PI / 2; horn.position.set(0.55, 0.77, 0); g.add(horn);
  } else {
    box(0.7, 0.7, 0.7, 0x6b4a2a, 0, 0.35, 0);                 // fallback crate
  }
  return g;
}

/** Make a prop translucent for the placement-mode ghost preview. */
export function ghostify(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const clone = (m: THREE.Material): THREE.Material => {
      const c = m.clone();
      c.transparent = true; c.opacity = 0.55; c.depthWrite = false;
      return c;
    };
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(clone) : clone(mesh.material);
  });
}

/** Flag the placement ghost as BLOCKED (overlapping another piece) — a red emissive glow — or clear it. */
export function setGhostBlocked(obj: THREE.Object3D, blocked: boolean): void {
  obj.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const std = m as THREE.MeshStandardMaterial;
      if (std.emissive) { std.emissive.setHex(blocked ? 0xff2222 : 0x000000); std.emissiveIntensity = blocked ? 0.7 : 1; }
    }
  });
}

export interface FurniturePlacement {
  bid: string;
  fid: string;
  itemId: string;
  x: number; // server px
  y: number; // server px
  z: number; // lift above the room floor (world units)
  rot: number; // yaw radians
}

interface Entry { obj: THREE.Group; itemId: string; parent: THREE.Group }

/** Mirrors the synced furniture placements into the scene (one prop per bid/fid). */
export class FurnitureLayer {
  private readonly entries = new Map<string, Entry>(); // `${bid}/${fid}`

  /** Reconcile the rendered set with `placements`. `groupFor` resolves a building's render group
   *  (null while its region isn't built — the piece is skipped and rendered on a later sync);
   *  `elevFor` gives the building's floor elevation (apartment storeys). */
  sync(
    placements: FurniturePlacement[],
    groupFor: (bid: string) => THREE.Group | null,
    elevFor: (bid: string) => number,
  ): void {
    const want = new Map<string, FurniturePlacement>();
    for (const p of placements) if (groupFor(p.bid)) want.set(`${p.bid}/${p.fid}`, p);

    for (const [key, e] of this.entries) {
      const p = want.get(key);
      // Drop pieces that vanished, changed item, or whose group was rebuilt (region reload).
      if (!p || p.itemId !== e.itemId || groupFor(p.bid) !== e.parent) {
        e.obj.removeFromParent();
        disposeProp(e.obj);
        this.entries.delete(key);
      }
    }
    for (const [key, p] of want) {
      let e = this.entries.get(key);
      if (!e) {
        const parent = groupFor(p.bid)!;
        const obj = furnitureMesh(p.itemId);
        obj.userData = { bid: p.bid, fid: p.fid, itemId: p.itemId };
        parent.add(obj);
        e = { obj, itemId: p.itemId, parent };
        this.entries.set(key, e);
      }
      e.obj.position.set(p.x * U, elevFor(p.bid) + p.z, p.y * U);
      e.obj.rotation.y = p.rot;
    }
  }

  /** The placement hit by a click ray (any piece, any distance — the caller range-checks). */
  pick(ray: THREE.Raycaster): { bid: string; fid: string; itemId: string; x: number; y: number; z: number } | null {
    for (const e of this.entries.values()) {
      if (!e.parent.visible) continue;
      if (ray.intersectObject(e.obj, true).length > 0) {
        const { bid, fid, itemId } = e.obj.userData as { bid: string; fid: string; itemId: string };
        return { bid, fid, itemId, x: e.obj.position.x / U, y: e.obj.position.z / U, z: 0 };
      }
    }
    return null;
  }

  /** The rendered prop for a placement (proximity checks), or null. */
  objectOf(bid: string, fid: string): THREE.Object3D | null {
    return this.entries.get(`${bid}/${fid}`)?.obj ?? null;
  }

  dispose(): void {
    for (const e of this.entries.values()) { e.obj.removeFromParent(); disposeProp(e.obj); }
    this.entries.clear();
  }
}

function disposeProp(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) m?.dispose();
  });
}
